/**
 * automations.ts — service layer for the Automation rule engine (Engine 5).
 *
 * A simple WHEN [event] IF [condition] THEN [action] engine. The shell
 * (AutomationPage) defined rules in the `automations` table but nothing fired
 * them. This adds the firing engine via APP-SIDE DISPATCH: event sites call
 * `runAutomations(event, context)` (e.g. leave.ts after a submit). It loads the
 * active rules for that trigger, evaluates conditions, executes actions, and
 * records an `automation_runs` row + bumps the rule's counters.
 *
 * Pure internal — NO external API: actions write to `notifications` (via the
 * shared sendNotification helper) and `tasks`. No edge function, no deploy.
 *
 * MVP actions: send_notification, create_task. Follow-ups: send_email (overlaps
 * the stubbed notification-email path), update_field, enroll_sequence (lands with
 * Nurturing). Unknown/unsupported action types are logged and skipped (they do
 * not fail the run).
 *
 * Requires migration 0006 (automations.conditions jsonb) for the IF support;
 * rules with conditions = null always fire.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { sendNotification } from './notifications';
import { getCurrentWorkspaceId, requireWorkspaceId } from './apiClient';
import { enrollContact } from './nurturing';
import type { NotificationType } from '../types';

export type ConditionOp =
  | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'is_empty' | 'is_not_empty';

export interface AutomationCondition {
  field: string;
  op?: ConditionOp;
  value: string | number | boolean | null;
}

export interface AutomationAction {
  type: string; // send_notification | create_activity | add_note | set_field | change_stage | assign_owner | create_task | …
  config: Record<string, any>;
}

interface AutomationRow {
  id: string;
  workspace_id: string | null;
  module?: string | null;
  trigger_event: string;
  trigger_config?: Record<string, any> | null;
  conditions: AutomationCondition | AutomationCondition[] | null;
  actions: AutomationAction[] | null;
  is_active: boolean;
  run_count: number | null;
}

// ─── System 4 — CRM record automation (extends the engine, module-keyed) ─────

export type RecordTrigger = 'record_created' | 'stage_changed' | 'field_changed' | 'record_updated';

/** The triggers offered by the CRM Automations builder. */
export const CRM_TRIGGERS: { value: RecordTrigger; label: string; hint: string }[] = [
  { value: 'record_created', label: 'Record created',       hint: 'When a new record is added' },
  { value: 'stage_changed',  label: 'Stage / status changed', hint: 'When it moves (optionally to a specific stage)' },
  { value: 'field_changed',  label: 'Field changed',         hint: 'When a chosen field changes' },
  { value: 'record_updated', label: 'Record updated',        hint: 'Any edit to the record' },
];

/** Condition operators offered by the builder (label + whether it needs a value). */
export const CONDITION_OPS: { value: ConditionOp; label: string; needsValue: boolean }[] = [
  { value: 'eq',           label: 'is',              needsValue: true },
  { value: 'neq',          label: 'is not',          needsValue: true },
  { value: 'gt',           label: 'greater than',    needsValue: true },
  { value: 'gte',          label: 'greater or equal', needsValue: true },
  { value: 'lt',           label: 'less than',       needsValue: true },
  { value: 'lte',          label: 'less or equal',   needsValue: true },
  { value: 'contains',     label: 'contains',        needsValue: true },
  { value: 'is_empty',     label: 'is empty',        needsValue: false },
  { value: 'is_not_empty', label: 'is not empty',    needsValue: false },
];

/** The v1 internal action types offered by the builder. */
export const CRM_ACTIONS: { value: string; label: string; hint: string }[] = [
  { value: 'create_activity',  label: 'Create activity',    hint: 'Add a task/call/email/meeting to the record' },
  { value: 'add_note',         label: 'Add note',           hint: 'Add a note to the record timeline' },
  { value: 'set_field',        label: 'Set a field',        hint: 'Change a built-in field value' },
  { value: 'change_stage',     label: 'Change stage/status', hint: 'Move the record to a stage' },
  { value: 'assign_owner',     label: 'Assign owner',       hint: 'Set the owner' },
  { value: 'send_notification', label: 'Send notification',  hint: 'In-app notification to a role/user' },
];

/** Per-module column map for record-mutating actions. Extend per module. */
const MODULE_MAP: Record<string, { table: string; stageColumn: string; ownerColumn: string }> = {
  crm_deals: { table: 'crm_deals', stageColumn: 'stage',  ownerColumn: 'owner_id' },
  crm_leads: { table: 'crm_leads', stageColumn: 'status', ownerColumn: 'assigned_to' },
};

/** Backstop: never run more than this many actions in a single pass. */
const MAX_ACTIONS_PER_PASS = 25;

export interface RunResult {
  matched: number;
  executed: number;
  skipped: number;
  failed: number;
}

/**
 * Pure: does the event `context` satisfy the rule's condition(s)?
 * null / empty conditions → always true. Supports a single condition or an
 * array (ALL must pass). Comparison is string-coerced so '5' matches 5.
 */
export function evaluateCondition(
  conditions: AutomationCondition | AutomationCondition[] | null | undefined,
  context: Record<string, any>,
): boolean {
  if (!conditions) return true;
  const list = Array.isArray(conditions) ? conditions : [conditions];
  if (list.length === 0) return true;
  return list.every((c) => {
    if (!c || !c.field) return true;
    const actual = context ? context[c.field] : undefined;
    return evalOp(c.op ?? 'eq', actual, c.value);
  });
}

const toNum = (x: unknown): number => {
  const n = parseFloat(String(x));
  return Number.isFinite(n) ? n : NaN;
};

/** One condition operator. String-coerced for eq/neq/contains; numeric for </> . */
function evalOp(op: ConditionOp, actual: unknown, value: unknown): boolean {
  switch (op) {
    case 'neq':          return String(actual) !== String(value);
    case 'gt':           return toNum(actual) >  toNum(value);
    case 'gte':          return toNum(actual) >= toNum(value);
    case 'lt':           return toNum(actual) <  toNum(value);
    case 'lte':          return toNum(actual) <= toNum(value);
    case 'contains':     return String(actual ?? '').toLowerCase().includes(String(value ?? '').toLowerCase());
    case 'is_empty':     return actual == null || String(actual).trim() === '';
    case 'is_not_empty': return !(actual == null || String(actual).trim() === '');
    case 'eq':
    default:             return String(actual) === String(value);
  }
}

/**
 * Fire every active rule for `event`: evaluate its condition, run its actions,
 * and log the outcome. Best-effort — a load error returns an empty result; a
 * single action failure marks that rule's run 'failed' but never throws.
 */
export async function runAutomations(
  event: string,
  context: Record<string, any> = {},
): Promise<RunResult> {
  const result: RunResult = { matched: 0, executed: 0, skipped: 0, failed: 0 };

  const { data, error } = await supabase
    .from('automations')
    .select('*')
    .eq('trigger_event', event)
    .eq('is_active', true)
    .eq('is_deleted', false);

  if (error) {
    console.warn('[automations] load failed:', error.message);
    return result;
  }

  const recordId = (context._record_id as string) ?? null;
  for (const rule of (data ?? []) as AutomationRow[]) {
    result.matched++;
    if (!evaluateCondition(rule.conditions, context)) {
      result.skipped++;
      await recordRun(rule, 'skipped', event, null, recordId);
      continue;
    }
    try {
      for (const action of rule.actions ?? []) {
        await executeAction(action, context, rule);
      }
      result.executed++;
      await recordRun(rule, 'success', event, null, recordId);
    } catch (err) {
      result.failed++;
      await recordRun(rule, 'failed', event, err instanceof Error ? err.message : String(err), recordId);
    }
  }

  return result;
}

/** Write an automation_runs row + bump the rule's run counters. Best-effort. */
async function recordRun(
  rule: AutomationRow,
  status: 'success' | 'failed' | 'skipped',
  event: string,
  errorMessage: string | null,
  recordId: string | null = null,
): Promise<void> {
  try {
    // The rule's own workspace is the authority. It is nullable in the schema, so fall back
    // to the signed-in person's workspace — the same one RLS let them read the rule through.
    // If neither is known, skip the history row rather than guess; the counters below still
    // update, because failing to record a run must never lose the run itself.
    const runWorkspaceId = rule.workspace_id ?? getCurrentWorkspaceId();
    if (!runWorkspaceId) {
      console.warn('[automations] automation_runs row skipped — no workspace on the rule or session');
    } else {
      const { error: insErr } = await supabase.from('automation_runs').insert({
        workspace_id: runWorkspaceId,
        automation_id: rule.id,
        status,
        triggered_by: event,
        error_message: errorMessage,
        record_id: recordId,               // which record the rule fired on (run-history label)
      });
      if (insErr) console.warn('[automations] automation_runs insert failed:', insErr.message);
    }
    await supabase
      .from('automations')
      .update({
        run_count: (rule.run_count ?? 0) + 1,
        last_run_at: new Date().toISOString(),
        last_run_status: status,
        updated_at: new Date().toISOString(),
      })
      .eq('id', rule.id);
  } catch (err) {
    console.warn('[automations] recordRun failed:', err);
  }
}

/** Execute one action. Throws on a real failure so the run is marked 'failed'. */
async function executeAction(
  action: AutomationAction,
  context: Record<string, any>,
  rule: AutomationRow,
): Promise<void> {
  const cfg = action?.config ?? {};
  switch (action?.type) {
    case 'send_notification': {
      const recipients = await resolveRecipients(cfg, context);
      if (recipients.length === 0) {
        // Benign — nobody matched the role / no target user. NOT an error.
        console.debug('[automations] send_notification: resolved 0 recipients for', cfg.role ?? cfg.user_id);
        return;
      }
      let sent = 0, failed = 0;
      for (const userId of recipients) {
        const result = await sendNotification({
          userId,
          type: ((cfg.type as string) || 'announcement') as NotificationType,
          title: String(cfg.title ?? 'Automation'),
          body: cfg.body ? String(cfg.body) : undefined,
        });
        if (result.ok) {
          sent++;
        } else {
          // Distinct from "no recipients" — a real insert failure, logged but
          // non-fatal so the remaining recipients still get their notification.
          failed++;
          console.error('[automations] send_notification: notification insert failed:', result.error);
        }
      }
      if (failed > 0) {
        console.error(`[automations] send_notification: ${failed}/${recipients.length} failed (${sent} sent)`);
      }
      return;
    }
    case 'create_task': {
      const { error } = await supabase.from('tasks').insert({
        workspace_id: rule.workspace_id ?? requireWorkspaceId(),
        title: String(cfg.title ?? 'Automated task'),
        description: cfg.description ? String(cfg.description) : null,
        status: 'todo',
        priority: (cfg.priority as string) || 'medium',
        // best-effort owner from the event context (may be null)
        reporter_id: context?.user_id ?? context?.actor_id ?? null,
      });
      if (error) throw new Error(`create_task failed: ${error.message}`);
      return;
    }
    case 'enroll_sequence': {
      // WHEN event → THEN enroll a contact into a nurture sequence (Engine 4 tie-in).
      const contactId = context?.contact_id ?? cfg.contact_id;
      if (!cfg.sequence_id) throw new Error('enroll_sequence: config.sequence_id is required');
      if (!contactId) {
        console.warn('[automations] enroll_sequence: no contact_id in event context — skipped');
        return;
      }
      await enrollContact(String(cfg.sequence_id), String(contactId));
      return;
    }
    // ── System 4 record actions (internal; need _module + _record_id) ──────
    case 'create_activity':
    case 'add_note': {
      const moduleKey = context._module as string;
      const recordId = context._record_id as string;
      if (!moduleKey || !recordId) throw new Error(`${action.type}: missing record context`);
      const isNote = action.type === 'add_note';
      const type = isNote ? 'note' : (String(cfg.activity_type || 'task'));
      const dueAt = cfg.due_in_days != null && cfg.due_in_days !== ''
        ? new Date(Date.now() + Number(cfg.due_in_days) * 86_400_000).toISOString()
        : null;
      const { error } = await supabase.from('activities').insert({
        workspace_id: rule.workspace_id ?? requireWorkspaceId(),
        module_key: moduleKey,
        record_id: recordId,
        actor_id: null,                       // system-created
        type,
        title: cfg.title ? String(cfg.title) : (isNote ? null : 'Automated task'),
        body: cfg.body ? String(cfg.body) : null,
        due_at: dueAt,
      });
      if (error) throw new Error(`${action.type} failed: ${error.message}`);
      return;
    }
    case 'set_field':
    case 'change_stage':
    case 'assign_owner': {
      const map = MODULE_MAP[context._module as string];
      if (!map) throw new Error(`${action.type}: unknown module "${context._module}"`);
      let column: string | undefined;
      let value: unknown;
      if (action.type === 'change_stage') { column = map.stageColumn; value = cfg.to; }
      else if (action.type === 'assign_owner') { column = map.ownerColumn; value = cfg.owner_id || null; }
      else { column = cfg.field ? String(cfg.field) : undefined; value = cfg.value ?? null; }
      if (!column) throw new Error(`${action.type}: missing target field`);
      const { error } = await supabase
        .from(map.table)
        .update({ [column]: value, updated_at: new Date().toISOString() })
        .eq('id', context._record_id);
      if (error) throw new Error(`${action.type} failed: ${error.message}`);
      return;
    }
    default:
      // send_email / unknown — not in this MVP.
      console.warn(`[automations] action "${action?.type}" not supported yet — skipped`);
      return;
  }
}

/**
 * Resolve send_notification recipients: by config.role (profiles with that role),
 * else the user referenced in the event context. Returns profile ids.
 */
async function resolveRecipients(
  cfg: Record<string, any>,
  context: Record<string, any>,
): Promise<string[]> {
  if (cfg.role) {
    // role is free-text in the UI and may be comma-separated ("hr_admin, manager").
    // Parse + match ANY of them so multi-role configs resolve all recipients.
    const roles = String(cfg.role).split(',').map((r) => r.trim()).filter(Boolean);
    if (roles.length === 0) return [];
    const { data, error } = await supabase.from('profiles').select('id').in('role', roles);
    if (error) {
      // Distinct from "no recipients" — a query failure must NOT be disguised
      // as a benign empty result. Still returns [] so the dispatch stays non-fatal.
      console.error('[automations] resolveRecipients: profiles query failed:', error.message);
      return [];
    }
    return ((data ?? []) as { id: string }[]).map((r) => r.id).filter(Boolean);
  }
  const target = context?.user_id ?? context?.actor_id ?? cfg.user_id;
  return target ? [String(target)] : [];
}

// ─── System 4 — module-keyed record dispatcher ───────────────────────────────

/** Does the rule's trigger_config refine-match this event?
 *  EXPORTED for the read-only preview (item 22) so "would this fire?" is answered by the
 *  same function the live dispatcher uses, not by a second copy that can drift from it. */
export function triggerConfigMatches(rule: Pick<AutomationRow, 'trigger_config'>, trigger: RecordTrigger, ctx: Record<string, any>): boolean {
  const cfg = rule.trigger_config ?? {};
  if (trigger === 'stage_changed' && cfg.to) {
    return String(ctx._to ?? ctx.stage ?? ctx.status ?? '') === String(cfg.to);
  }
  if (trigger === 'field_changed' && cfg.field) {
    return Array.isArray(ctx._changed) && ctx._changed.includes(cfg.field);
  }
  return true;
}

/**
 * Fire the record rules for a module + trigger. Loads active rules where
 * module = moduleKey AND trigger_event = trigger, matches trigger_config,
 * evaluates conditions against the record, runs actions, logs runs. Reuses the
 * engine's evaluateCondition / executeAction / recordRun.
 *
 * LOOP GUARD: the action writes (set_field / change_stage / …) go straight to
 * Supabase and never call back into this dispatcher — only the user-initiated
 * write paths do — so a rule's actions can't trigger another pass. Best-effort:
 * never throws to the caller.
 */
export async function runRecordAutomations(
  moduleKey: string,
  recordId: string,
  trigger: RecordTrigger,
  context: Record<string, any> = {},
): Promise<RunResult> {
  const result: RunResult = { matched: 0, executed: 0, skipped: 0, failed: 0 };
  try {
    const { data, error } = await supabase
      .from('automations')
      .select('*')
      .eq('module', moduleKey)
      .eq('trigger_event', trigger)
      .eq('is_active', true)
      .eq('is_deleted', false);
    if (error) {
      console.warn('[automations] record load failed:', error.message);
      return result;
    }

    const ctx = { ...context, _module: moduleKey, _record_id: recordId };
    for (const rule of (data ?? []) as AutomationRow[]) {
      if (!triggerConfigMatches(rule, trigger, ctx)) continue;
      result.matched++;
      if (!evaluateCondition(rule.conditions, ctx)) {
        result.skipped++;
        await recordRun(rule, 'skipped', trigger, null, recordId);
        continue;
      }
      try {
        let n = 0;
        for (const action of rule.actions ?? []) {
          if (n++ >= MAX_ACTIONS_PER_PASS) break;
          await executeAction(action, ctx, rule);
        }
        result.executed++;
        await recordRun(rule, 'success', trigger, null, recordId);
      } catch (err) {
        result.failed++;
        await recordRun(rule, 'failed', trigger, err instanceof Error ? err.message : String(err), recordId);
      }
    }
  } catch (err) {
    console.warn('[automations] runRecordAutomations threw (non-fatal):', err);
  }
  return result;
}

// ─── CRUD + hooks for the CRM Automations UI ─────────────────────────────────

export interface AutomationRule {
  id: string;
  workspace_id: string | null;
  module: string | null;
  name: string;
  description: string | null;
  trigger_event: string;
  trigger_config: Record<string, any> | null;
  conditions: AutomationCondition[] | null;
  actions: AutomationAction[] | null;
  is_active: boolean;
  run_count: number | null;
  last_run_at: string | null;
  last_run_status: string | null;
  created_at: string;
  updated_at: string;
}

export interface NewRuleInput {
  name: string;
  description?: string;
  trigger_event: RecordTrigger;
  trigger_config?: Record<string, any>;
  conditions?: AutomationCondition[];
  actions?: AutomationAction[];
}

export type RulePatch = Partial<Pick<AutomationRule,
  'name' | 'description' | 'trigger_event' | 'trigger_config' | 'conditions' | 'actions' | 'is_active'>>;

export async function listRules(moduleKey: string): Promise<AutomationRule[]> {
  const { data, error } = await supabase
    .from('automations')
    .select('*')
    .eq('module', moduleKey)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as AutomationRule[]) ?? [];
}

export async function createRule(moduleKey: string, input: NewRuleInput): Promise<AutomationRule> {
  const { data, error } = await supabase
    .from('automations')
    .insert({
      workspace_id: requireWorkspaceId(),
      module: moduleKey,
      name: (input.name ?? '').trim() || 'Untitled rule',
      description: input.description?.trim() || null,
      trigger_event: input.trigger_event,
      trigger_config: input.trigger_config ?? {},
      conditions: input.conditions ?? [],
      actions: input.actions ?? [],
      is_active: true,
    })
    .select()
    .single();
  if (error) throw error;
  return data as AutomationRule;
}

export async function updateRule(id: string, patch: RulePatch): Promise<void> {
  const { error } = await supabase
    .from('automations')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function setRuleActive(id: string, active: boolean): Promise<void> {
  await updateRule(id, { is_active: active });
}

export async function deleteRule(id: string): Promise<void> {
  const { error } = await supabase
    .from('automations')
    .update({ is_deleted: true, deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface AutomationRunRow {
  id: string;
  automation_id: string;
  record_id: string | null;
  status: string;
  triggered_by: string | null;
  error_message: string | null;
  created_at: string;
  automation?: { name: string | null; module: string | null } | null;
  /** Display name of the deal/lead the rule fired on (resolved from record_id). */
  record_name?: string | null;
}

export async function listRuns(moduleKey: string, limit = 50): Promise<AutomationRunRow[]> {
  // automation_runs.automation_id has NO foreign key, so a PostgREST
  // `automations!inner(...)` embed can't resolve (it errored → empty panel).
  // Resolve the module's rule ids first, then read their runs and map names.
  const { data: ruleRows, error: rErr } = await supabase
    .from('automations')
    .select('id, name')
    .eq('module', moduleKey)
    .eq('is_deleted', false);
  if (rErr) throw rErr;
  const rules = (ruleRows ?? []) as { id: string; name: string }[];
  if (rules.length === 0) return [];
  const nameById = new Map(rules.map(r => [r.id, r.name]));
  const { data, error } = await supabase
    .from('automation_runs')
    .select('*')
    .in('automation_id', rules.map(r => r.id))
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  const runs = (data ?? []) as AutomationRunRow[];

  // Resolve each run's record_id → a display name (deal title / lead name), so the
  // history shows WHICH record fired. Older runs (pre-record_id) just stay unlabelled.
  const recordNames = await resolveRecordNames(moduleKey, runs.map(r => r.record_id));

  return runs.map(r => ({
    ...r,
    automation: { name: nameById.get(r.automation_id) ?? null, module: moduleKey },
    record_name: r.record_id ? (recordNames.get(r.record_id) ?? null) : null,
  }));
}

/** Map a module's record ids → human names (crm_deals.title / crm_leads full name). */
async function resolveRecordNames(
  moduleKey: string,
  ids: (string | null)[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const unique = Array.from(new Set(ids.filter((id): id is string => !!id)));
  if (unique.length === 0) return out;
  const table = MODULE_MAP[moduleKey]?.table;
  if (!table) return out;
  const cols = moduleKey === 'crm_leads' ? 'id, first_name, last_name, company' : 'id, title';
  const { data, error } = await supabase.from(table).select(cols).in('id', unique);
  if (error) {
    console.warn('[automations] record-name resolve failed:', error.message);
    return out;
  }
  for (const row of (data ?? []) as any[]) {
    const name = moduleKey === 'crm_leads'
      ? ([row.first_name, row.last_name].filter(Boolean).join(' ').trim() || row.company || '')
      : (row.title || '');
    if (name) out.set(row.id, name);
  }
  return out;
}

// ─── Which stage does a rule ACT ON? (item 21) ───────────────────────────────
/**
 * The board's automation panel groups rules by the stage they act on, so you can see at a
 * glance which stages have automation and which don't. This is that question, answered in
 * one place because both the grouping and its heading counts must agree.
 *
 * Two ways a rule can name a stage, in priority order:
 *   1. it TRIGGERS on movement into one — `stage_changed` with `trigger_config.to`. This is
 *      the strongest claim and wins outright.
 *   2. it is CONDITIONAL on being in one — a plain `stage is X` condition.
 *
 * Anything else is not stage-specific (a rule on every record created, a rule on value) and
 * returns null, which the panel groups separately rather than filing under a stage it has
 * nothing to do with.
 */
export function ruleStageKey(
  rule: Pick<AutomationRule, 'trigger_event' | 'trigger_config' | 'conditions'>,
  moduleKey: string,
): string | null {
  const cfg = rule.trigger_config ?? {};
  if (rule.trigger_event === 'stage_changed' && cfg.to) return String(cfg.to);
  const stageColumn = MODULE_MAP[moduleKey]?.stageColumn;
  if (!stageColumn) return null;
  const list = Array.isArray(rule.conditions) ? rule.conditions : (rule.conditions ? [rule.conditions] : []);
  const hit = list.find(c => c && c.field === stageColumn && (c.op ?? 'eq') === 'eq' && c.value != null && c.value !== '');
  return hit ? String(hit.value) : null;
}

// ─── TEST MODE — a preview that writes nothing (item 22) ─────────────────────
/**
 * WHY THIS IS A PREVIEW AND NOT AN ENGINE REWRITE.
 *
 * The spec asked me to check first whether evaluating a rule can be separated from running
 * it, and to report the cost if it could not. It already is:
 *
 *   • `evaluateCondition` is a PURE function of (conditions, context) — no I/O at all.
 *   • `triggerConfigMatches` is likewise pure, and is now exported so the preview asks the
 *     same question the dispatcher asks rather than a lookalike.
 *   • every WRITE lives in `executeAction` and `recordRun`, which nothing below calls.
 *
 * So the split was already there and this is a new READ-ONLY path over it, not a refactor.
 *
 * WHAT THIS FUNCTION MAY DO: SELECT. It reads the record, and for a notification action it
 * reads which profiles hold the named role so it can say who would be told. Nothing else.
 * No record changes, no task, no notification, no `automation_runs` row, no `run_count`
 * bump. Running it a hundred times leaves the database exactly as it was.
 */

export interface PreviewAction {
  type: string;
  /** Plain language: what this action would do to THIS record. */
  text: string;
  /** False when the engine has no implementation for this action type — it would be
   *  skipped in a real run too, and the preview says so rather than inventing an effect. */
  supported: boolean;
}

export interface RulePreview {
  wouldFire: boolean;
  /** Plain language: why it would fire, or the reason it would not. */
  reason: string;
  actions: PreviewAction[];
  /** The record this was evaluated against, by name. */
  recordName: string;
  /** What the preview had to ASSUME, because a test cannot make the trigger happen for
   *  real (e.g. "assuming the Value field has just changed"). Empty when it assumed nothing. */
  assumptions: string[];
}

/** Display helpers the caller already has loaded — passed in so the preview fires no extra
 *  queries for names it can be handed. */
export interface PreviewLookups {
  stageName: (key: string) => string;
  personName: (id: string) => string;
  fieldLabel: (key: string) => string;
}

const DEFAULT_LOOKUPS: PreviewLookups = {
  stageName: k => k,
  personName: id => id,
  fieldLabel: k => k,
};

/** A record you can point the test at. Live rows only — testing against a deleted deal
 *  would answer a question nobody asked. */
export interface PreviewRecordOption {
  id: string;
  name: string;
  /** The record's current stage/status key, so the picker can show it beside the name. */
  stage: string | null;
}

export async function listPreviewRecords(moduleKey: string, limit = 100): Promise<PreviewRecordOption[]> {
  const map = MODULE_MAP[moduleKey];
  if (!map) return [];
  const cols = moduleKey === 'crm_leads'
    ? `id, first_name, last_name, company, ${map.stageColumn}`
    : `id, title, ${map.stageColumn}`;
  const { data, error } = await supabase
    .from(map.table)
    .select(cols)
    .eq('is_deleted', false)
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return ((data ?? []) as any[]).map(r => ({
    id: r.id,
    name: moduleKey === 'crm_leads'
      ? ([r.first_name, r.last_name].filter(Boolean).join(' ').trim() || r.company || 'Unnamed lead')
      : (r.title || 'Untitled deal'),
    stage: r[map.stageColumn] ?? null,
  }));
}

/** Human wording for one condition operator, in the same order CONDITION_OPS declares. */
const opPhrase = (op: ConditionOp): string =>
  CONDITION_OPS.find(o => o.value === op)?.label ?? String(op);

/** "—" for an absent value, so a sentence never reads "…is null". */
const shown = (v: unknown): string => {
  if (v == null || String(v).trim() === '') return 'empty';
  return String(v);
};

/** Format a condition/action value using the right lookup for the field it belongs to. */
function valueText(moduleKey: string, field: string, value: unknown, lk: PreviewLookups): string {
  const map = MODULE_MAP[moduleKey];
  if (map && field === map.stageColumn) return lk.stageName(String(value ?? ''));
  if (map && field === map.ownerColumn) return value ? lk.personName(String(value)) : 'nobody';
  return shown(value);
}

/** Describe ONE action in plain language. Pure — it never performs the action. */
export function describeAction(
  action: AutomationAction,
  moduleKey: string,
  record: Record<string, any>,
  lk: PreviewLookups = DEFAULT_LOOKUPS,
  recipientNames: string[] = [],
): PreviewAction {
  const cfg = action?.config ?? {};
  const type = action?.type ?? '';
  switch (type) {
    case 'create_activity': {
      const kind = String(cfg.activity_type || 'task');
      const title = cfg.title ? `“${cfg.title}”` : 'with no title';
      const due = cfg.due_in_days != null && cfg.due_in_days !== ''
        ? `, due in ${cfg.due_in_days} day${Number(cfg.due_in_days) === 1 ? '' : 's'}`
        : '';
      return { type, supported: true, text: `Add a ${kind} ${title} to this record’s timeline${due}.` };
    }
    case 'add_note':
      return {
        type, supported: true,
        text: cfg.body ? `Add a note to this record’s timeline: “${cfg.body}”.` : 'Add an empty note to this record’s timeline.',
      };
    case 'set_field': {
      const field = String(cfg.field ?? '');
      const label = lk.fieldLabel(field);
      const from = valueText(moduleKey, field, record?.[field], lk);
      const to = valueText(moduleKey, field, cfg.value, lk);
      if (!field) return { type, supported: false, text: 'Set a field — but no field is chosen, so nothing would happen.' };
      return { type, supported: true, text: `Change ${label} from ${from} to ${to}.` };
    }
    case 'change_stage': {
      const map = MODULE_MAP[moduleKey];
      const to = cfg.to ? lk.stageName(String(cfg.to)) : '';
      const from = map ? lk.stageName(String(record?.[map.stageColumn] ?? '')) : '';
      if (!to) return { type, supported: false, text: 'Move the record to a stage — but no stage is chosen, so nothing would happen.' };
      if (from === to) return { type, supported: true, text: `Move it to ${to} — it is already there, so nothing would change.` };
      return { type, supported: true, text: `Move it from ${from} to ${to}.` };
    }
    case 'assign_owner': {
      const who = cfg.owner_id ? lk.personName(String(cfg.owner_id)) : 'nobody (unassigned)';
      return { type, supported: true, text: `Set the owner to ${who}.` };
    }
    case 'send_notification': {
      const title = cfg.title ? `“${cfg.title}”` : 'an untitled notification';
      if (recipientNames.length === 0) {
        return { type, supported: true, text: `Send ${title} — but nobody matches the chosen recipient, so no notification would be sent.` };
      }
      return { type, supported: true, text: `Send ${title} to ${recipientNames.join(', ')}.` };
    }
    case 'create_task':
      return { type, supported: true, text: `Create a task “${cfg.title ?? 'Automated task'}”${cfg.priority ? ` at ${cfg.priority} priority` : ''}.` };
    case 'enroll_sequence':
      return { type, supported: true, text: 'Enrol the linked contact into a nurture sequence.' };
    default:
      // Mirrors executeAction's default arm exactly: logged and skipped, never run.
      return { type, supported: false, text: `“${type || 'unnamed action'}” is not something this app can do yet, so it would be skipped.` };
  }
}

/** Read WHO a send_notification would reach, by name. SELECT only. */
async function previewRecipientNames(cfg: Record<string, any>): Promise<string[]> {
  if (cfg.role) {
    const roles = String(cfg.role).split(',').map(r => r.trim()).filter(Boolean);
    if (roles.length === 0) return [];
    const { data, error } = await supabase.from('profiles').select('full_name').in('role', roles);
    if (error) {
      console.warn('[automations] preview recipients query failed:', error.message);
      return [];
    }
    return ((data ?? []) as { full_name: string | null }[]).map(r => r.full_name || 'Someone').filter(Boolean);
  }
  return [];
}

/**
 * Evaluate `rule` against a REAL record and report, in plain language, what would happen.
 * WRITES NOTHING — see the note at the top of this section.
 */
export async function previewRule(
  rule: Pick<AutomationRule, 'trigger_event' | 'trigger_config' | 'conditions' | 'actions'>,
  moduleKey: string,
  recordId: string,
  lookups: PreviewLookups = DEFAULT_LOOKUPS,
): Promise<RulePreview> {
  const lk = lookups;
  const map = MODULE_MAP[moduleKey];
  const assumptions: string[] = [];

  if (!map) {
    return { wouldFire: false, reason: `This app doesn’t know how to read “${moduleKey}” records, so the rule can’t be tested.`, actions: [], recordName: '', assumptions };
  }

  const { data, error } = await supabase.from(map.table).select('*').eq('id', recordId).maybeSingle();
  if (error || !data) {
    return { wouldFire: false, reason: 'That record couldn’t be read, so the rule can’t be tested against it.', actions: [], recordName: '', assumptions };
  }
  const record = data as Record<string, any>;
  const recordName = moduleKey === 'crm_leads'
    ? ([record.first_name, record.last_name].filter(Boolean).join(' ').trim() || record.company || 'this lead')
    : (record.title || 'this deal');

  const trigger = rule.trigger_event as RecordTrigger;
  const cfg = rule.trigger_config ?? {};
  const stageValue = record[map.stageColumn] ?? null;

  /* THE TRIGGER.
     A test cannot make the trigger actually happen — nothing is being created, moved or
     edited. So a refinement that can be checked against the record AS IT STANDS is checked
     (a stage rule against the record's current stage, which is the case the spec's own
     example names), and one that cannot is ASSUMED and said out loud. */
  if (trigger === 'stage_changed' && cfg.to) {
    if (!triggerConfigMatches(rule, trigger, { ...record, stage: stageValue, status: stageValue })) {
      return {
        wouldFire: false,
        recordName,
        assumptions,
        actions: [],
        reason: `This rule would not fire — ${recordName}’s stage is ${lk.stageName(String(stageValue ?? ''))}, and this rule only runs on ${lk.stageName(String(cfg.to))}.`,
      };
    }
    assumptions.push(`Assuming ${recordName} has just moved into ${lk.stageName(String(cfg.to))}.`);
  } else if (trigger === 'stage_changed') {
    assumptions.push(`Assuming ${recordName}’s stage has just changed. This rule runs on any stage change.`);
  } else if (trigger === 'field_changed') {
    const f = cfg.field ? lk.fieldLabel(String(cfg.field)) : 'a field';
    assumptions.push(`Assuming ${f} on ${recordName} has just changed.`);
  } else if (trigger === 'record_created') {
    assumptions.push(`Assuming ${recordName} has just been created.`);
  } else {
    assumptions.push(`Assuming ${recordName} has just been edited.`);
  }

  // THE CONDITIONS — the same pure evaluator the live engine uses, one at a time so the
  // answer can name the condition that failed rather than shrugging.
  const conditions = Array.isArray(rule.conditions) ? rule.conditions : (rule.conditions ? [rule.conditions] : []);
  const ctx = { ...record, _module: moduleKey, _record_id: recordId };
  for (const c of conditions) {
    if (!c || !c.field) continue;
    if (evaluateCondition([c], ctx)) continue;
    const label = lk.fieldLabel(c.field);
    const actual = valueText(moduleKey, c.field, record[c.field], lk);
    const op = opPhrase(c.op ?? 'eq');
    const needsValue = CONDITION_OPS.find(o => o.value === (c.op ?? 'eq'))?.needsValue ?? true;
    const want = needsValue ? ` ${valueText(moduleKey, c.field, c.value, lk)}` : '';
    return {
      wouldFire: false,
      recordName,
      assumptions,
      actions: [],
      reason: `This rule would not fire — ${recordName}’s ${label} is ${actual}, and this rule only runs when ${label} ${op}${want}.`,
    };
  }

  // WHAT IT WOULD DO. Described, never performed.
  const list = rule.actions ?? [];
  const actions: PreviewAction[] = [];
  for (const a of list) {
    const names = a?.type === 'send_notification' ? await previewRecipientNames(a.config ?? {}) : [];
    actions.push(describeAction(a, moduleKey, record, lk, names));
  }

  return {
    wouldFire: true,
    recordName,
    assumptions,
    actions,
    reason: actions.length === 0
      ? 'This rule would fire, but it has no actions, so nothing would happen.'
      : 'This rule would fire.',
  };
}

export const rulesKey = (moduleKey: string) => ['automation_rules', moduleKey] as const;
export const runsKey = (moduleKey: string) => ['automation_runs_log', moduleKey] as const;

export function useAutomationRules(moduleKey: string) {
  const q = useQuery({ queryKey: rulesKey(moduleKey), queryFn: () => listRules(moduleKey), staleTime: 30_000 });
  return { rules: q.data ?? [], isLoading: q.isLoading, refetch: q.refetch };
}

export function useAutomationRuns(moduleKey: string) {
  const q = useQuery({ queryKey: runsKey(moduleKey), queryFn: () => listRuns(moduleKey), staleTime: 15_000 });
  return { runs: q.data ?? [], isLoading: q.isLoading, refetch: q.refetch };
}
